/**
 * ConvaiService - Manages conversations with Hope AI companion
 *
 * Features:
 * - Crisis detection (local keyword matching — instant, no network)
 * - Conversation storage in Firebase (HIPAA-compliant)
 * - Session management
 *
 * Note: Chat messages now route through Claude via /api/generate-notes?mode=hope-chat
 * (handled directly in HopeContextChat.tsx). This service retains crisis detection
 * and conversation storage utilities.
 */

import { collection, addDoc, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  StoredConversation,
  CrisisDetection,
} from '../types/convai';
import { CRISIS_RESOURCES, CONVERSATION_STORAGE } from '../config/convai';

/**
 * Detect crisis keywords in patient message with improved categorization
 */
export const detectCrisis = (message: string): CrisisDetection => {
  const lowerMessage = message.toLowerCase();
  const detectedKeywords: string[] = [];

  // Critical severity - immediate danger keywords
  const criticalKeywords = [
    'kill myself',
    'end my life',
    'suicide',
    'suicidal',
    'want to die',
    'better off dead',
    'no reason to live',
    'planning to die',
    'going to kill',
    'plan to end',
  ];

  // High severity - self-harm or severe distress
  const highKeywords = [
    'hurt myself',
    'self-harm',
    'self harm',
    'cutting',
    'harm myself',
    'end it all',
    "can't go on",
    'no point in living',
    'want to hurt',
    'overdose',
  ];

  // Medium severity - significant distress
  const mediumKeywords = [
    'hopeless',
    'worthless',
    'give up',
    "can't take it anymore",
    'overwhelming',
    'unbearable',
    'no hope',
    'want to disappear',
  ];

  // Check for critical keywords
  for (const keyword of criticalKeywords) {
    if (lowerMessage.includes(keyword)) {
      detectedKeywords.push(keyword);
    }
  }
  if (detectedKeywords.length > 0) {
    return {
      detected: true,
      keywords: detectedKeywords,
      severity: 'critical',
      recommendedAction: 'emergency',
    };
  }

  // Check for high severity
  for (const keyword of highKeywords) {
    if (lowerMessage.includes(keyword)) {
      detectedKeywords.push(keyword);
    }
  }
  if (detectedKeywords.length > 0) {
    return {
      detected: true,
      keywords: detectedKeywords,
      severity: 'high',
      recommendedAction: 'notify_provider',
    };
  }

  // Check for medium severity
  for (const keyword of mediumKeywords) {
    if (lowerMessage.includes(keyword)) {
      detectedKeywords.push(keyword);
    }
  }
  if (detectedKeywords.length > 0) {
    return {
      detected: true,
      keywords: detectedKeywords,
      severity: 'medium',
      recommendedAction: 'provide_resources',
    };
  }

  // No crisis detected
  return {
    detected: false,
    keywords: [],
    severity: 'low',
    recommendedAction: 'monitor',
  };
};

/**
 * Get crisis resources message
 */
export const getCrisisResourcesMessage = (): string => {
  return `I'm really concerned about what you just shared. Your safety is the most important thing.

Please reach out to one of these resources immediately:

🔴 **${CRISIS_RESOURCES.lifeline.name}**
   Call or text: ${CRISIS_RESOURCES.lifeline.phone}
   Available 24/7

🔴 **${CRISIS_RESOURCES.crisisTextLine.name}**
   Text: ${CRISIS_RESOURCES.crisisTextLine.text}
   Available 24/7

🔴 **${CRISIS_RESOURCES.emergency.name}**
   Call: ${CRISIS_RESOURCES.emergency.phone}
   For immediate danger

Your therapist will also be notified. You're not alone, and help is available right now.`;
};

/**
 * Store conversation in Firebase (HIPAA-compliant)
 */
export const storeConversation = async (
  patientId: string,
  sessionId: string,
  userMessage: string,
  aiResponse: string,
  crisisDetected: boolean,
  crisisKeywords?: string[]
): Promise<void> => {
  try {
    const conversationData: Omit<StoredConversation, 'id'> = {
      patientId,
      sessionId,
      userMessage,
      aiResponse,
      timestamp: new Date(),
      crisisDetected,
      crisisKeywords: crisisKeywords || [],
      providerNotified: crisisDetected, // Auto-notify provider on crisis
      patientConsent: true, // TODO: Check actual consent status
    };

    await addDoc(collection(db, CONVERSATION_STORAGE.collectionName), {
      ...conversationData,
      timestamp: Timestamp.fromDate(conversationData.timestamp),
    });

  } catch (error) {
    console.error('Error storing conversation:', error);
    // Don't throw - we don't want to break the chat if storage fails
  }
};

/**
 * Get conversation history for a patient
 */
export const getConversationHistory = async (
  patientId: string,
  limit: number = 50
): Promise<StoredConversation[]> => {
  try {
    const q = query(
      collection(db, CONVERSATION_STORAGE.collectionName),
      where('patientId', '==', patientId),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const conversations: StoredConversation[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      conversations.push({
        id: doc.id,
        patientId: data.patientId,
        sessionId: data.sessionId,
        userMessage: data.userMessage,
        aiResponse: data.aiResponse,
        timestamp: data.timestamp.toDate(),
        crisisDetected: data.crisisDetected || false,
        crisisKeywords: data.crisisKeywords || [],
        providerNotified: data.providerNotified || false,
        patientConsent: data.patientConsent || false,
      });
    });

    return conversations.slice(0, limit);
  } catch (error) {
    console.error('Error fetching conversation history:', error);
    return [];
  }
};

/**
 * Generate unique session ID
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate Convai configuration (legacy — kept for HopeAICompanion.tsx compatibility)
 */
export const validateConvaiSetup = (_apiKey: string, _characterId: string): boolean => {
  console.warn('Convai is deprecated — Hope now uses Claude. validateConvaiSetup always returns true.');
  return true;
};

/**
 * Legacy stub — HopeAICompanion.tsx imports this. Real chat now goes through HopeContextChat → Claude.
 */
export const sendMessageToHope = async (
  _message: string,
  _characterId: string,
  _apiKey: string,
  _sessionId: string = '-1'
): Promise<{ text: string; sessionId: string }> => {
  return { text: 'Hope has been upgraded! Please use the main Ask Hope chat.', sessionId: '-1' };
};

export const sendMessageToHopeWithContext = async (
  _message: string,
  _characterId: string,
  _apiKey: string,
  _context: string,
  _sessionId: string = '-1'
): Promise<{ text: string; sessionId: string }> => {
  return { text: 'Hope has been upgraded! Please use the main Ask Hope chat.', sessionId: '-1' };
};
